import axios from 'axios';
import type {
  StockHistoryResponse,
  DetectionRequest,
  DetectionResponse,
  BulkDetectionRequest,
  BulkDetectionResponse
} from '../types/api';
import type { AlgorithmInfo } from '../utils/algorithmProvider';

// API基本設定
// 本番環境: REACT_APP_API_URL=/api (Nginxが /api を /バックエンド にルーティング)
// 開発環境: REACT_APP_API_URL=http://localhost:8001
export const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30秒タイムアウト（5000銘柄処理を考慮）
  headers: {
    'Content-Type': 'application/json',
  },
});

// エラーハンドリング用インターセプター
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    throw error;
  }
);

export class StockAnalysisAPI {
  /**
   * 利用可能なアルゴリズム一覧を取得
   */
  static async getAlgorithms(): Promise<AlgorithmInfo[]> {
    const response = await apiClient.get<AlgorithmInfo[]>('/algorithms');
    return response.data;
  }

  /**
   * 単一銘柄の株価履歴を取得
   */
  static async getStockHistory(symbol: string, days: number = 30): Promise<StockHistoryResponse> {
    const response = await apiClient.get<StockHistoryResponse>(`/stocks/${symbol}/history`, {
      params: { days }
    });
    return response.data;
  }

  /**
   * 単一銘柄の変化点検出を実行
   */
  static async detectChangePoint(request: DetectionRequest): Promise<DetectionResponse> {
    const response = await apiClient.post<DetectionResponse>('/detect', request);
    return response.data;
  }

  /**
   * 複数銘柄の一括変化点検出を実行
   */
  static async detectChangePointsBulk(request: BulkDetectionRequest): Promise<BulkDetectionResponse> {
    const response = await apiClient.post<BulkDetectionResponse>('/detect/bulk', request);
    return response.data;
  }

  /**
   * NASDAQ全銘柄の変化点検出を実行
   */
  static async detectAllNasdaq(
    algorithmName: string, 
    days: number = 30,
    algorithmParams?: Record<string, any>
  ): Promise<BulkDetectionResponse> {
    const response = await apiClient.post<BulkDetectionResponse>('/detect/nasdaq', {
      algorithm_name: algorithmName,
      days,
      algorithm_params: algorithmParams
    });
    return response.data;
  }

  /**
   * APIヘルスチェック
   */
  static async healthCheck(): Promise<{ status: string; message: string }> {
    const response = await apiClient.get<{ status: string; message: string }>('/health');
    return response.data;
  }
}

// デフォルトエクスポート
export default StockAnalysisAPI;